import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, Users, Mail, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import MatchScoreBar from './MatchScoreBar';
import EmailPreviewModal from './EmailPreviewModal';
import { supabase } from '../../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * CandidateMatchTable - Display matched candidates with scores
 *
 * Props:
 * - rfqId: UUID of the RFQ
 * - onCandidatesSelected: Callback(candidateIds) when selection changes
 */
const CandidateMatchTable = ({ rfqId, onCandidatesSelected }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [minScore, setMinScore] = useState(0);
  const [includeOpenSoon, setIncludeOpenSoon] = useState(false);
  const [highlightThreshold, setHighlightThreshold] = useState(60);
  const [quickEmailCandidateId, setQuickEmailCandidateId] = useState(null);
  const [outreachTemplate, setOutreachTemplate] = useState(null);

  useEffect(() => {
    fetchHighlightThreshold();
  }, []);

  const fetchHighlightThreshold = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('recruiter_settings')
        .select('highlight_min_match_pct')
        .eq('user_id', user.id)
        .maybeSingle();

      if (data?.highlight_min_match_pct != null) {
        setHighlightThreshold(data.highlight_min_match_pct);
      }
    } catch (err) {
      console.error('Error fetching highlight threshold:', err);
    }
  };

  const openQuickEmail = async (candidateId) => {
    try {
      if (!outreachTemplate) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Authentication required');

        const response = await fetch(`${API_URL}/matching/email-templates?type=candidate_individual_outreach`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        const data = await response.json();
        const defaultTemplate = (data.templates || []).find(t => t.is_default) || data.templates?.[0];
        if (!defaultTemplate) {
          setError('No candidate outreach email template found - set one up in Settings > Email Templates');
          return;
        }
        setOutreachTemplate(defaultTemplate);
      }
      setQuickEmailCandidateId(candidateId);
    } catch (err) {
      console.error('Error preparing quick email:', err);
      setError(err.message);
    }
  };

  const handleSearch = async () => {
    try {
      setSearching(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Authentication required');
      }

      const queryParams = new URLSearchParams({
        includeOpenSoon: includeOpenSoon.toString(),
        minScore: minScore.toString()
      });

      const response = await fetch(`${API_URL}/matching/rfq/${rfqId}/candidates?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMatches(data.matches || []);
        setLoading(false);
      } else {
        throw new Error(data.message || 'Failed to search candidates');
      }
    } catch (err) {
      console.error('Error searching candidates:', err);
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedCandidateIds.length === matches.length) {
      // Deselect all
      setSelectedCandidateIds([]);
      if (onCandidatesSelected) onCandidatesSelected([]);
    } else {
      // Select all
      const allIds = matches.map(m => m.candidateId);
      setSelectedCandidateIds(allIds);
      if (onCandidatesSelected) onCandidatesSelected(allIds);
    }
  };

  const handleToggleCandidate = (candidateId) => {
    let newSelection;
    if (selectedCandidateIds.includes(candidateId)) {
      newSelection = selectedCandidateIds.filter(id => id !== candidateId);
    } else {
      newSelection = [...selectedCandidateIds, candidateId];
    }
    setSelectedCandidateIds(newSelection);
    if (onCandidatesSelected) onCandidatesSelected(newSelection);
  };

  const clearanceLevelToText = (level) => {
    const map = { 0: 'None', 1: 'Baseline', 2: 'NV1', 3: 'NV2', 4: 'TSPV' };
    return map[level] || 'None';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Candidate Matches
            </CardTitle>
            <CardDescription>
              {matches.length > 0
                ? `${matches.length} candidate${matches.length !== 1 ? 's' : ''} found${selectedCandidateIds.length > 0 ? ` • ${selectedCandidateIds.length} selected` : ''}`
                : 'Search for candidates matching this RFQ'
              }
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            {/* Filters */}
            <div className="flex items-center space-x-2 mr-4">
              <label className="flex items-center text-sm space-x-2">
                <input
                  type="checkbox"
                  checked={includeOpenSoon}
                  onChange={(e) => setIncludeOpenSoon(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span>Include "Open Soon"</span>
              </label>
              <label className="flex items-center text-sm space-x-2">
                <span>Min Score:</span>
                <input
                  type="number"
                  value={minScore}
                  onChange={(e) => setMinScore(parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                  className="w-16 border rounded px-2 py-1 bg-background"
                />
              </label>
            </div>
            <Button
              onClick={handleSearch}
              disabled={searching}
            >
              <Search className={`h-4 w-4 mr-2 ${searching ? 'animate-spin' : ''}`} />
              Search
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4 bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {searching && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Searching candidates...</p>
          </div>
        )}

        {!searching && !loading && matches.length === 0 && (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No candidates found</p>
            <p className="text-sm text-muted-foreground mt-2">
              Click "Search" to find matching candidates
            </p>
          </div>
        )}

        {!searching && matches.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedCandidateIds.length === matches.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30px]">
                      <input
                        type="checkbox"
                        checked={selectedCandidateIds.length === matches.length && matches.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </TableHead>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Match Score</TableHead>
                    <TableHead>Clearance</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Availability</TableHead>
                    <TableHead>Matched Skills</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matches.map((match) => {
                    const isHighlighted = match.scorePercent >= highlightThreshold;
                    return (
                    <TableRow
                      key={match.candidateId}
                      className={
                        selectedCandidateIds.includes(match.candidateId)
                          ? 'bg-accent'
                          : isHighlighted
                            ? 'bg-amber-50 dark:bg-amber-950/20'
                            : ''
                      }
                    >
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedCandidateIds.includes(match.candidateId)}
                          onChange={() => handleToggleCandidate(match.candidateId)}
                          className="rounded border-gray-300"
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5">
                          {isHighlighted && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500 shrink-0" />}
                          {match.candidate?.first_name} {match.candidate?.last_name}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {match.candidate?.email}
                        </span>
                      </TableCell>
                      <TableCell className="min-w-[150px]">
                        <MatchScoreBar score={match.scorePercent} size="sm" />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col space-y-1">
                          <Badge variant="outline" className="text-xs w-fit">
                            {clearanceLevelToText(match.candidate?.clearance_level || 0)}
                          </Badge>
                          {match.clearanceOk && (
                            <span className="text-xs text-green-600">✓ Meets requirement</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm">
                            {match.candidate?.location_city && match.candidate?.location_state
                              ? `${match.candidate.location_city}, ${match.candidate.location_state}`
                              : match.candidate?.location_city || match.candidate?.location_state || '-'}
                          </span>
                          {match.locationOk && (
                            <span className="text-xs text-green-600">✓ Matches target</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            match.candidate?.availability_status === 'open'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }
                        >
                          {match.candidate?.availability_status || 'Unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col space-y-1 max-w-[200px]">
                          {match.matchedRequiredSkills && match.matchedRequiredSkills.length > 0 && (
                            <div>
                              <span className="text-xs font-medium text-green-700 dark:text-green-400">
                                Required: {match.matchedRequiredCount ?? match.matchedRequiredSkills.length}
                                {match.totalRequiredCount ? ` of ${match.totalRequiredCount}` : ''}
                              </span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {match.matchedRequiredSkills.slice(0, 3).map((skill, idx) => (
                                  <Badge key={idx} className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                    {skill.name || skill}
                                  </Badge>
                                ))}
                                {match.matchedRequiredSkills.length > 3 && (
                                  <span className="text-xs text-muted-foreground">
                                    +{match.matchedRequiredSkills.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          {match.matchedNiceSkills && match.matchedNiceSkills.length > 0 && (
                            <div>
                              <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                                Nice: {match.matchedNiceCount ?? match.matchedNiceSkills.length}
                                {match.totalNiceCount ? ` of ${match.totalNiceCount}` : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant={isHighlighted ? 'default' : 'outline'}
                          onClick={() => openQuickEmail(match.candidateId)}
                        >
                          <Mail className="h-3.5 w-3.5 mr-1.5" />
                          Email
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>

      {quickEmailCandidateId && outreachTemplate && (
        <EmailPreviewModal
          open={!!quickEmailCandidateId}
          onOpenChange={(open) => !open && setQuickEmailCandidateId(null)}
          template={outreachTemplate}
          rfqId={rfqId}
          candidateId={quickEmailCandidateId}
        />
      )}
    </Card>
  );
};

export default CandidateMatchTable;
